/**
 * Link-preview tags (Open Graph, read by Telegram, X, Slack, Discord and others): the page's
 * title and description with a 1200x630 card of the brand in the page's language.
 */
import { LOCALES } from '../i18n/config';
import { SITE_URL } from '../site';

const OG_LOCALES: Record<string, string> = { en: 'en_US', hi: 'hi_IN' };

export interface PreviewTag {
  property?: string;
  name?: string;
  content: string;
}

interface Preview {
  locale: string;
  title: string;
  description?: string;
  /** The page's canonical address; left out for pages that have none (the dashboard). */
  url?: string;
  /** What the card shows, for people who can't see it: the landing's headline. */
  imageAlt: string;
}

export function previewTags({ locale, title, description, url, imageAlt }: Preview): PreviewTag[] {
  const ogLocale = (lang: string) => OG_LOCALES[lang] ?? lang;
  return [
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'LowCostRouter' },
    { property: 'og:title', content: title },
    ...(description === undefined ? [] : [{ property: 'og:description', content: description }]),
    ...(url === undefined ? [] : [{ property: 'og:url', content: url }]),
    { property: 'og:locale', content: ogLocale(locale) },
    ...LOCALES.filter((lang) => lang !== locale).map((lang) => ({
      property: 'og:locale:alternate',
      content: ogLocale(lang),
    })),
    { property: 'og:image', content: `${SITE_URL}/og-${locale}.png` },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: imageAlt },
    { name: 'twitter:card', content: 'summary_large_image' },
  ];
}

const escapeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The same tags as HTML, for the pages written without React (`/` and the dashboard shell). */
export function previewHtml(tags: PreviewTag[]): string {
  return tags
    .map((tag) => {
      const key =
        tag.property === undefined ? `name="${tag.name ?? ''}"` : `property="${tag.property}"`;
      return `<meta ${key} content="${escapeAttribute(tag.content)}">`;
    })
    .join('');
}

export { escapeAttribute };
